import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Space, Input, Row, Col } from 'antd';
import { Redirect } from 'react-router-dom';

import { CommonFormProps, OnboardingLayout } from '.';
import { createPath } from '~/pages/onboarding/Sider';
import { BackButton, CountryInput, Form } from '~/components';
import { createBusiness } from '~/api/onboarding';
import { Address } from '~/types';
import { auth } from '~/api/axios';
import { useOnboardingPage, useOnboardingState } from '~/hooks/onboarding';
import onError from '~/errorsHandler';

const { Title, Text } = Typography;

const CompanyAddress: FC<CommonFormProps> = (props) => {
  const { name } = props;
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation('onboarding');
  const dt = (key: string): string => t(`translation:${key}`);
  const [form] = Form.useForm();
  const onboardingPage = useOnboardingPage(name);

  const [pageState, setPageState] = useOnboardingState(name);
  const [onboardingState, , setOnboardingState] = useOnboardingState();
  const [countryState] = useOnboardingState('country');
  const [companyInformationState] = useOnboardingState('companyInformation');
  const [companyContactsState] = useOnboardingState('companyInformation/contacts');

  const countryId = countryState.formData?.country;

  const disableCurrentAndPreviousPages = () => {
    const currentPageIndex = onboardingState.findIndex((item) => item.pageName === name);
    setOnboardingState((prev) => {
      return prev.map((item, index) => {
        if (index > currentPageIndex) return item;
        return {
          ...item,
          disabled: true,
        };
      });
    });
  };

  const onFinish = async (values: Address) => {
    setLoading(true);
    try {
      const {
        data: { id: businessId, solutionId },
      } = await createBusiness({
        country: countryId,
        address: values,
        contacts: Object.entries(companyContactsState.formData).map(([channel, id]) => ({
          channel,
          id,
        })),
        ...companyInformationState.formData,
      });
      auth.setProfile(businessId);
      auth.setSolution(solutionId);

      setPageState((prev) => ({
        ...prev,
        formData: values,
      }));
      onboardingPage.activateNext();
      onboardingPage.goNext();
      disableCurrentAndPreviousPages();
    } catch (error: any) {
      onError(error);
    }
    setLoading(false);
  };

  return (
    <OnboardingLayout>
      {pageState.skip && <Redirect to={createPath('personalInfo')} />}
      <Title level={2}>{t('companyAddressHeader')}</Title>
      <Space size="middle" direction="vertical">
        <Text>{t('companyAddressSubtitle')}</Text>
        <Form
          form={form}
          name={name}
          initialValues={pageState.formData || { country: countryId }}
          layout="vertical"
          size="large"
          onFinish={onFinish}
        >
          <Form.Item name="country" rules={[{ required: true }]}>
            <CountryInput disabled />
          </Form.Item>
          <Form.Item name="postalCode">
            <Input placeholder={dt('postalCode')} />
          </Form.Item>
          <Form.Item name="region" rules={[{ required: true }]}>
            <Input placeholder={dt('region')} />
          </Form.Item>
          <Form.Item name="city" rules={[{ required: true }]}>
            <Input placeholder={dt('city')} />
          </Form.Item>
          <Form.Item name="addressLine" rules={[{ required: true }]}>
            <Input.TextArea placeholder={dt('address')} />
          </Form.Item>
          <Row gutter={16}>
            <Col>
              <BackButton onClick={onboardingPage.goBack} />
            </Col>
            <Col>
              <Form.Item noStyle>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  {dt('continue')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Space>
    </OnboardingLayout>
  );
};

export default CompanyAddress;
